import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { contactRoles, errors, fields, personObj, OTHER_PLACEHOLDER } from '../../../constants';
import { personEntityTitle } from '../../../utils/helper';
import { isDesignatedContactComplete } from '../../../utils/completes';
import { isEmail, isPhoneWithCountry } from '../../../utils/validations';
import { ProfileContext } from '../../../context/Profile.context';
import { affilations } from '../../../constants/companyAffilations';
import { Lock, Trash2, User, UserCheck } from 'lucide-react';
import Controls from '../../Controls';
import { PhoneInputField } from '../../PhoneInputField';
import PrivateInformation from '../../PrivateInformation/PrivateInformation';
import PreviousAdded from '../../PreviousAdded';
import RemovePerson from '../../RemovePerson';
import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, EntityLabel, EntityLabelGroup, SectionTitle, SelectField, TextField, Typography, Label, InfoPanel } from '@otcmarketsgroup/otcm-ui-lib';

const PRIMARY_CONTACT_PARAM = fields.PRIMARY_BILLING;
const ADDITIONAL_CONTACT_PARAM = fields.OTHER_BILLING;
const PREV_ADDED_VIEW = 'previousAdded';
const FORM_VIEW = 'formView';
const PRIMARY_BILLING = 'Primary Billing Contact';
const ADDITIONAL_BILLING = 'Additional Billing Contact';
const REMOVE_PERSON_VIEW = 'removePerson';

const BillingContactsTab = ({ onPersonAdd, onPersonUpdate, onPersonRemove }) => {
  const [profileState] = useContext(ProfileContext);
  const [contactView, setContactView] = useState(personObj);
  const [dialog, setDialog] = useState(false);
  const [viewHistory, setViewHistory] = useState(null);
  const [validationError, setValidationError] = useState(false);
  const peopleList = profileState.profile.peopleList;
  const primaryBillingContact = peopleList.filter(person => person[PRIMARY_CONTACT_PARAM]);
  const additionalBillingList = peopleList.filter(person => person[ADDITIONAL_CONTACT_PARAM]);
  const prevAddedList = peopleList.filter(person => (!person[PRIMARY_CONTACT_PARAM] && !person[ADDITIONAL_CONTACT_PARAM]) && (person.isOfficer || person.isDirector || person.isAuthorizedUser || person.isPrimaryContact));
  const previousAddView = !contactView.id && prevAddedList && prevAddedList.length > 0;
  const contactTotal = peopleList.filter(person => person[PRIMARY_CONTACT_PARAM]).length;
  const [slideView, setSlideView] = useState(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
  const isExistingContact = contactView && (contactView.isOfficer || contactView.isDirector);
  const isAdd = !contactView.id || ((contactView && !contactView[PRIMARY_CONTACT_PARAM]) && (contactView && !contactView[ADDITIONAL_CONTACT_PARAM]));
  const isAccountPayable = contactView.isAccountPayable || (contactView.firstName === 'Accounts' && contactView.lastName === 'Payable');
  const hasAccountPayable = peopleList.find(person => !person.isDeleted && (person.isAccountPayable || (person.firstName === 'Accounts' && person.lastName === 'Payable')));
  const isReadOnly = profileState.readOnly;
  const isPrimaryBillingView = dialog === PRIMARY_BILLING;
  const isAdditionalBillingView = dialog === ADDITIONAL_BILLING;
  const isRemoveView = slideView === REMOVE_PERSON_VIEW;
  const dialogTitle = isRemoveView ? `Remove - ${personEntityTitle(contactView, false)}` : isAdd ? `Add ${dialog}` : personEntityTitle(contactView, false);
  let CONTACT_PARAM;
  let CURRENT_VIEW;

  if (isPrimaryBillingView) {
    CONTACT_PARAM = PRIMARY_CONTACT_PARAM;
    CURRENT_VIEW = PRIMARY_BILLING;
  }
  if (isAdditionalBillingView) {
    CONTACT_PARAM = ADDITIONAL_CONTACT_PARAM;
    CURRENT_VIEW = ADDITIONAL_BILLING;
  }

  useEffect(() => {
    if (!dialog) {
      setContactView(personObj);
      setSlideView(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
      setValidationError(false);
    }
  }, [dialog, previousAddView]);

  const onFieldChange = (field, value) => {
    setContactView({
      ...contactView,
      [field]: value
    });
  };

  const onPhoneValueChanged = (phone, data) => {
    setContactView({
      ...contactView,
      phone,
      phoneCountryCode: data?.countryCode ? data.countryCode.toUpperCase() : contactView?.phoneCountryCode
    });
  };

  const onAffiliationChange = value => {
    setContactView({
      ...contactView,
      title1: value === OTHER_PLACEHOLDER ? OTHER_PLACEHOLDER : value
    });
  };

  const onAccountPayableChanged = checked => {
    if (checked === true) {
      setContactView(prevState => {
        return {
          ...prevState,
          firstName: 'Accounts',
          middleName: null,
          lastName: 'Payable',
          title1: null,
          isAccountPayable: true
        };
      });
    } else {
      setContactView(prevState => {
        return {
          ...prevState,
          firstName: null,
          lastName: null,
          title1: null,
          isAccountPayable: false
        };
      });
    }
  };

  const handleEdit = (person, panelType) => {
    const selectedContactParam = panelType === PRIMARY_BILLING ? PRIMARY_CONTACT_PARAM : ADDITIONAL_CONTACT_PARAM;

    setSlideView(FORM_VIEW);
    setContactView({ ...person });
    setValidationError(!isDesignatedContactComplete(person, selectedContactParam));
    setDialog(panelType);
  };

  const handleCancelRemove = () => {
    setDialog(false);
  };

  const handleRemove = (person, param) => {
    onPersonRemove(person, param);
  };

  const handleRemoveCustom = (person, panelType) => {
    setContactView(person);
    setSlideView(REMOVE_PERSON_VIEW);
    setDialog(panelType);
  };

  const isCustomRemove = person => {
    const totalRoles = contactRoles.filter(role => person[role]);

    return totalRoles.length > 1;
  };

  const onRemoveSubmit = person => {
    onPersonUpdate(person);
    setDialog(false);
  };

  const validateComplete = (person, param) => {
    return isDesignatedContactComplete(person, param);
  };

  const handleCancelView = () => {
    viewHistory === PREV_ADDED_VIEW && prevAddedList && prevAddedList.length > 0 ? setSlideView(PREV_ADDED_VIEW) : setDialog(false);
    setContactView(personObj);
  };

  const handleSubmitPerson = () => {
    setValidationError(false);

    const contact = contactView;
    const onSubmit = !contact.id ? onPersonAdd : onPersonUpdate;

    contact[CONTACT_PARAM] = true;

    if (!isDesignatedContactComplete(contact, CONTACT_PARAM)) {
      setValidationError(true);
      return;
    }

    onSubmit(contact);
    setDialog(false);
  };

  const handleSelectPrevContact = person => {
    const newContact = person;
    setViewHistory(PREV_ADDED_VIEW);
    setContactView(newContact);
    setSlideView(FORM_VIEW);
  };

  const handleAddNew = () => {
    setViewHistory(PREV_ADDED_VIEW);
    setSlideView(FORM_VIEW);
  };

  const renderPreviouslyAdded = () => (
    <>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body1'>
            {`Select an individual previously entered, or Add a New ${CURRENT_VIEW}.`}
          </Typography>
          <PreviousAdded
            listTitle='List of Individuals Previously Entered'
            items={prevAddedList}
            handleSelect={handleSelectPrevContact} />
          <Button
            fullWidth
            onClick={handleAddNew}>
            {`Add New ${CURRENT_VIEW}`}
          </Button>
        </Box>
      </DialogContent>
      <Controls
        cancelText='Cancel'
        showSubmit={false}
        onCancelClick={() => setDialog(false)} />
    </>
  );

  const dialogClose = () => {
    setDialog(false);
  };

  const renderAddNew = () => {
    const isPresetAffiliation = affilations.some(item => item.value === contactView?.title1);
    const showAffiliationInput = !isExistingContact && !isAccountPayable && (contactView?.title1 === OTHER_PLACEHOLDER || (contactView?.title1 && !isPresetAffiliation));
    const affiliationMissing = validationError && !isExistingContact && !isAccountPayable && !contactView?.title1;
    const emailInvalid = validationError && !!(contactView?.email && !isEmail(contactView?.email));
    const emailMissing = validationError && !contactView?.email;
    const phoneBlank = validationError && (!contactView?.phone || !contactView?.phoneCountryCode);
    const phoneInvalid = validationError && !!(contactView?.phone && contactView?.phoneCountryCode && !isPhoneWithCountry(contactView?.phone, contactView?.phoneCountryCode));
    const phoneErrorText = phoneBlank ? errors.INCOMPLETE_DATA_FIELD : phoneInvalid ? errors.INVALID_PHONE : '';

    return <>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PrivateInformation />
          {!isExistingContact && <>
            <Typography variant='body1'>
              Enter title and name of {CURRENT_VIEW}.
            </Typography>
            {(isAdditionalBillingView && !hasAccountPayable && !contactView[fields.PRIMARY_CONTACT] && !contactView[fields.AUTH_USER]) && <Checkbox
              label='This is an Accounts Payable Contact.'
              checked={!!isAccountPayable}
              disabled={isReadOnly}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontWeight: 700,
                  fontSize: '1rem',
                },
              }}
              onChange={e => onAccountPayableChanged(e.target.checked)} />}
            <Box display='flex' flexDirection='column' gap={2}>
              <TextField
                label='First Name'
                value={isAccountPayable ? 'N/A' : contactView?.firstName || ''}
                readOnly={isReadOnly}
                disabled={isAccountPayable}
                placeholder='Enter text here'
                maxLength={75}
                showClearButton
                error={(validationError || isReadOnly) && !isAccountPayable && !contactView?.firstName}
                helperText={(validationError || isReadOnly) && !isAccountPayable && !contactView?.firstName ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onFieldChange('firstName', e.target.value)}
              />
              <TextField
                label='Middle Name'
                value={isAccountPayable ? 'N/A' : contactView?.middleName || ''}
                readOnly={isReadOnly}
                disabled={isAccountPayable}
                placeholder='Enter text here'
                maxLength={75}
                showClearButton
                onChange={e => onFieldChange('middleName', e.target.value)}
              />
              <TextField
                label='Last Name'
                value={isAccountPayable ? 'N/A' : contactView?.lastName || ''}
                readOnly={isReadOnly}
                disabled={isAccountPayable}
                placeholder='Enter text here'
                maxLength={75}
                showClearButton
                error={(validationError || isReadOnly) && !isAccountPayable && !contactView?.lastName}
                helperText={(validationError || isReadOnly) && !isAccountPayable && !contactView?.lastName ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onFieldChange('lastName', e.target.value)}
              />
            </Box>
          </>}
          <Typography variant='body1'>
            Enter phone number and email for {CURRENT_VIEW}.
          </Typography>
          <Box display='flex' flexDirection='column' gap={2}>
            <Box>
              <Label startIcon={<Lock size='16px' color='var(--otc-green)' />}>
                Phone Number
              </Label>
              <PhoneInputField
                value={contactView?.phone || ''}
                country={contactView?.phoneCountryCode?.toLowerCase()}
                error={phoneBlank || phoneInvalid}
                errorText={phoneErrorText}
                onChange={onPhoneValueChanged}
              />
            </Box>
            <Box>
              <Label startIcon={<Lock size='16px' color='var(--otc-green)' />}>
                Email
              </Label>
              <TextField
                value={contactView?.email || ''}
                readOnly={isReadOnly}
                placeholder='Enter text here'
                maxLength={75}
                showClearButton
                error={emailMissing || emailInvalid}
                helperText={emailMissing ? errors.INCOMPLETE_DATA_FIELD : emailInvalid ? errors.INVALID_EMAIL : ''}
                onChange={e => onFieldChange('email', e.target.value)}
              />
            </Box>
            {!isExistingContact && <>
              <SelectField
                label='Company Affiliation'
                placeholder={isAccountPayable && 'N/A'}
                value={contactView?.title1 ? (isPresetAffiliation ? contactView.title1 : OTHER_PLACEHOLDER) : ''}
                options={affilations}
                readOnly={isReadOnly}
                disabled={isAccountPayable}
                error={affiliationMissing}
                helperText={affiliationMissing ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onAffiliationChange(e.target.value)}
              />
              {showAffiliationInput && <TextField
                label='Enter other affiliation'
                value={contactView?.title1 === OTHER_PLACEHOLDER ? '' : (contactView?.title1 || '')}
                readOnly={isReadOnly}
                placeholder='Enter text here'
                showClearButton
                maxLength={75}
                error={(validationError || isReadOnly) && contactView?.title1 === OTHER_PLACEHOLDER}
                helperText={(validationError || isReadOnly) && contactView?.title1 === OTHER_PLACEHOLDER ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onFieldChange('title1', e.target.value)}
              />}
            </>}
          </Box>
          {validationError && <InfoPanel isWarning title={errors.INCOMPLETE_DATA} />}
        </Box>
      </DialogContent>
      {!isReadOnly && <Controls
        cancelText='Cancel'
        submitText={isAdd ? 'Add' : 'Update'}
        onCancelClick={handleCancelView}
        onSubmitClick={handleSubmitPerson}
      />}
    </>;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {contactTotal < 1 && <InfoPanel isWarning title='One Primary Billing Contact is required.' />}
      <Typography variant='body1'>
        All billing notices will be sent by email addressed to the Primary Billing Contact with Additional Billing Contact(s) copied.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <SectionTitle noUnderline>{PRIMARY_BILLING}</SectionTitle>
            {(contactTotal < 1 && !isReadOnly) && <Button variant='text' onClick={() => setDialog(PRIMARY_BILLING)}>
              Add {PRIMARY_BILLING}
            </Button>}
          </Box>

          {primaryBillingContact.length === 0 && <Typography variant='body2'>
            No Primary Billing Contact has been added yet.
          </Typography>}

          <EntityLabelGroup>
            {primaryBillingContact.map((person, index) => {
              const complete = validateComplete(person, PRIMARY_CONTACT_PARAM);

              return <EntityLabel
                key={`billing-primary-${person.id || index}`}
                title={personEntityTitle(person, false)}
                subtitle={isReadOnly ? 'View' : complete ? 'Complete' : 'Enter Information'}
                icon={complete ? <UserCheck /> : <User />}
                actionIcon={<Trash2 />}
                isComplete={complete}
                isWarning={!complete}
                onEntityClick={() => handleEdit(person, PRIMARY_BILLING)}
                onActionClick={!isReadOnly ? () => handleRemoveCustom(person, PRIMARY_BILLING) : undefined} />;
            })}
          </EntityLabelGroup>
        </Box>

        <Box>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <SectionTitle noUnderline>Additional Billing Contacts</SectionTitle>
            {!isReadOnly && <Button variant='text' onClick={() => setDialog(ADDITIONAL_BILLING)}>
              Add {ADDITIONAL_BILLING}
            </Button>}
          </Box>

          {additionalBillingList.length === 0 && <Typography variant='body2'>
            No Additional Billing Contacts have been added yet.
          </Typography>}

          <EntityLabelGroup>
            {additionalBillingList.map((person, index) => {
              const complete = validateComplete(person, ADDITIONAL_CONTACT_PARAM);

              return <EntityLabel
                key={`billing-additional-${person.id || index}`}
                title={personEntityTitle(person, false)}
                subtitle={isReadOnly ? 'View' : complete ? 'Complete' : 'Enter Information'}
                icon={complete ? <UserCheck /> : <User />}
                actionIcon={<Trash2 />}
                isComplete={complete}
                isWarning={!complete}
                onEntityClick={() => handleEdit(person, ADDITIONAL_BILLING)}
                onActionClick={!isReadOnly ? () => handleRemoveCustom(person, ADDITIONAL_BILLING) : undefined} />;
            })}
          </EntityLabelGroup>
        </Box>
      </Box>
      <Dialog open={!!dialog} onClose={dialogClose}>
        <DialogTitle title={dialogTitle} onClose={dialogClose} />
        {slideView === PREV_ADDED_VIEW && renderPreviouslyAdded()}
        {slideView === FORM_VIEW && renderAddNew()}
        {isRemoveView && <RemovePerson
          person={{ ...contactView }}
          selectedRemove={CONTACT_PARAM}
          onCancel={handleCancelRemove}
          onRemoveSubmit={onRemoveSubmit} />}
      </Dialog>
    </Box>
  );
};

BillingContactsTab.propTypes = {
  onPersonAdd: PropTypes.func,
  onPersonUpdate: PropTypes.func,
  onPersonRemove: PropTypes.func
};

export default BillingContactsTab;
